import { createFileRoute } from "@tanstack/react-router";
import { ExternalLink, MapPin, TrainFront } from "lucide-react";
import { Button, Section } from "@alight/ui";
import { useSeo } from "../lib/seo";
import { useBlock } from "../lib/content";
import { PageIntro } from "../components/PageIntro";
import { JsonLd } from "../components/JsonLd";
import { placesJsonLd } from "../lib/structured-data";

export const Route = createFileRoute("/find-us")({
  component: FindUsPage,
});

/** A Google Maps embed needs no API key for a plain place search. */
function mapEmbedSrc(query: string): string {
  return `https://www.google.com/maps?q=${encodeURIComponent(query)}&output=embed`;
}

function LocationCard({
  region,
  church,
  address,
  mapUrl,
  image,
  imageAlt,
  note,
  eager,
}: {
  region: string;
  church: string;
  address: string;
  mapUrl: string;
  image: string;
  imageAlt: string;
  note: string;
  eager?: boolean;
}) {
  return (
    <article className="overflow-hidden rounded-card border border-border bg-card shadow-soft">
      <div className={image ? "grid md:grid-cols-2" : "grid"}>
        {image && (
          <img
            src={image}
            alt={imageAlt}
            width={1000}
            height={640}
            loading={eager ? "eager" : "lazy"}
            className="h-56 w-full object-cover md:h-full"
          />
        )}
        <iframe
          title={`Map — ${church}`}
          src={mapEmbedSrc(`${church}, ${address}`)}
          loading="lazy"
          referrerPolicy="no-referrer-when-downgrade"
          className="h-56 w-full border-0 md:h-full"
        />
      </div>
      <div className="flex flex-col gap-4 p-6 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <p className="text-sm font-bold uppercase tracking-wide text-primary">{region}</p>
          <h2 className="mt-1 font-heading text-2xl font-bold text-ink">{church}</h2>
          <p className="mt-1 flex items-start gap-2 text-muted">
            <MapPin className="mt-1 h-4 w-4 shrink-0" aria-hidden />
            {address}
          </p>
          {note && (
            <p className="mt-1 flex items-center gap-2 text-sm text-muted">
              <TrainFront className="h-4 w-4 shrink-0" aria-hidden />
              {note}
            </p>
          )}
        </div>
        {mapUrl && (
          <Button asChild variant="outline" size="sm" className="shrink-0">
            <a href={mapUrl} target="_blank" rel="noreferrer">
              Google Maps
              <ExternalLink className="ml-1.5 h-3.5 w-3.5" aria-hidden />
            </a>
          </Button>
        )}
      </div>
    </article>
  );
}

function FindUsPage() {
  const c = useBlock("locations");
  const banners = useBlock("banners");

  useSeo({
    title: "Find us | Connect Sydney",
    description:
      "Connect runs at three locations across Sydney — Eastwood (North), Hurstville (South) and Cabramatta (West) — each a short walk from the station, with Campus Time near Wynyard in the city.",
    path: "/find-us",
    image: banners.findUsImage,
  });

  return (
    <>
      <JsonLd data={placesJsonLd(c)} />
      <PageIntro
        eyebrow="Our locations"
        title="Find us"
        description={c.intro}
        image={banners.findUsImage}
        imageAlt={banners.findUsImageAlt}
      />

      <Section tone="paper" spacing="lg">
        <div className="space-y-8">
          {c.items.map((l, i) => (
            <LocationCard key={i} {...l} eager={i === 0} />
          ))}
        </div>
      </Section>

      <Section tone="card" spacing="lg">
        <div className="mx-auto max-w-2xl text-center">
          <h2 className="font-heading text-3xl font-extrabold text-ink">{c.campusTimeTitle}</h2>
          <p className="mt-4 text-muted">{c.campusTimeNote}</p>
        </div>
        <div className="mt-10">
          <LocationCard
            region="Campus Time"
            church={c.campusTimeChurch}
            address={c.campusTimeAddress}
            mapUrl={c.campusTimeMapUrl}
            image={c.campusTimeImage}
            imageAlt={c.campusTimeImageAlt}
            note=""
          />
        </div>
      </Section>
    </>
  );
}
